import React, { memo, useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Flex, Box, H1, Button, Text } from "ui-components";
import { identityVerificationStatus } from "ducks/actions";
import NotEligible from "components/NotEligible";
import {
  makeSelectIdentityVerificationStatus,
  makeSelectIdentityVerificationStatusFailed,
  makeSelectReuploadIdentityUrl,
  makeSelectIsFetchingData,
  makeSelectSessionToken
} from "ducks/selectors";
import Spinner, { styles } from "components/Spinner";
import { gotoUrl, urlParam } from "utils/helpers";
import {
  paths,
  PAYMENT_STATUS_POLL_INTERVAL,
  IDENTITY_CHECK_POLL_MAX_COUNT
} from "utils/constants";

// TODO: Use localisation
export const IdentityVerification = props => {
  const {
    isFetchingData,
    fetchIdentityVerificationStatus,
    identityVerificationStatus,
    identityVerificationStatusFailed,
    reuploadIdentityUrl,
    token
  } = props;
  const [pollCount, setPollCount] = useState(0);

  useEffect(
    () => {
      const toPoll =
        pollCount === 0 ||
        (!identityVerificationStatusFailed &&
          identityVerificationStatus === null &&
          pollCount < IDENTITY_CHECK_POLL_MAX_COUNT);
      if (toPoll && token) {
        setTimeout(() => {
          setPollCount(pollCount + 1);
          fetchIdentityVerificationStatus(token);
        }, PAYMENT_STATUS_POLL_INTERVAL);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pollCount]
  );

  useEffect(() => {
    if (identityVerificationStatus) {
      const paymentReturnPath =
        urlParam("return_url") ||
        paths.PAYMENT_RETURN.replace(":channel", "web");
      gotoUrl(paymentReturnPath, true);
    }
  }, [identityVerificationStatus]);
  if (!token) {
    return <NotEligible />;
  } else if (identityVerificationStatus) {
    // Identity verification success - redirecting to payment return page
    return (
      <Box variant="main" padding={[6, 8]}>
        <H1 textAlign="center" mb={8}>
          Document successfully verified. Redirecting..
        </H1>
        <Spinner size={200} sx={styles.modalContainer} />
      </Box>
    );
  } else if (
    identityVerificationStatus === false ||
    pollCount >= IDENTITY_CHECK_POLL_MAX_COUNT
  ) {
    return (
      <Box variant="main" padding={[6, 8]}>
        <H1 mb={4}>Document verification failed</H1>
        <Text sx={{ mb: 8 }}>
          If your ID type is Australian Driver's License or Passport, please
          ensure these fields are correct:
        </Text>
        <ol>
          <li>ID Number</li>
          <li>First Name as shown in ID</li>
          <li>Middle Name (If shown in ID)</li>
          <li>Last Name as shown in ID</li>
          <li>Date of Birth</li>
        </ol>
        <Text sx={{ my: 8 }}>
          If your ID Type is International Passport, please ensure that your
          filled name matches your passport's Machine-Readable Zone.
        </Text>
        <Flex justifyContent="center">
          <Button
            onClick={() => gotoUrl(`${reuploadIdentityUrl}?reset=true`, true)}
          >
            Edit Personal Details
          </Button>
        </Flex>
      </Box>
    );
  } else if (
    isFetchingData ||
    pollCount === 0 ||
    identityVerificationStatus === null
  ) {
    return (
      <Box variant="main" padding={[6, 8]}>
        <H1 textAlign="center" mb={8}>
          Document verification in progress
        </H1>
        <Spinner size={200} sx={styles.modalContainer} />
      </Box>
    );
  } else if (identityVerificationStatusFailed) {
    return (
      <Box variant="main" padding={[6, 8]} textAlign="center">
        <H1 mb={6}>Document verification failed</H1>
        Please reach out to customer happiness team.
      </Box>
    );
  } else {
    return <Spinner size={200} sx={styles.modalContainer} />;
  }
};

const mapStateToProps = createStructuredSelector({
  isFetchingData: makeSelectIsFetchingData("identityVerificationStatus"),
  identityVerificationStatus: makeSelectIdentityVerificationStatus(),
  identityVerificationStatusFailed: makeSelectIdentityVerificationStatusFailed(),
  reuploadIdentityUrl: makeSelectReuploadIdentityUrl(),
  token: makeSelectSessionToken()
});

export function mapDispatchToProps(dispatch) {
  return {
    fetchIdentityVerificationStatus: token => {
      const params = { token };
      dispatch(identityVerificationStatus(params));
    }
  };
}

IdentityVerification.defaultProps = {
  makeSelectIsIdentityVerified: () => {}
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect, memo)(IdentityVerification);
